import { useState } from "react";
import { useInView } from "react-intersection-observer";
import { RouteFactory } from "@app/routePaths";
import { HomeContext } from "@app/types/Page";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import HomepageMasthead from "@components/Header/HomepageMasthead";
import MetaTags from "@components/MetaTags/MetaTags";
import MyBTHomeCarousel from "@components/MyBT/MyBTHomeCarousel";
import SGBudget2026Banner from "@components/NewsBar/components/SGBudget2026Banner";
import NewsBar from "@components/NewsBar/NewsBar";
import HomepageCarouselNewsletter from "@components/Newsletter/HomepageCarouselNewsletter";
import { NEWSLETTER_TYPE } from "@components/Newsletter/types/Newsletter";
import HomeSkybox from "@components/Skybox/HomeSkybox";
import { myBTRecommendedKeywords } from "@features/mybt/constants";
import { useGetPosthogVariant } from "@features/posthog/hooks/useGetPosthogVariant";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useHomepageRedirect } from "@hooks/useHomepageRedirect";
import BreakingNews from "@pages/Home/BreakingNews/BreakingNews";
import CompaniesMarkets from "@pages/Home/CompaniesMarkets/CompaniesMarkets";
import Lifestyle from "@pages/Home/Lifestyle/Lifestyle";
import HomepageTopOverview from "@pages/Home/Overview/HomepageTopOverview";
import Podcasts from "@pages/Home/Podcasts/Podcasts";
import Verticals from "@pages/Home/Verticals/Verticals";
import Video from "@pages/Home/Video/Video";
import SubMobileNavigation from "@particles/Navigation/components/SubMobileNavigation";
import { useWindowSize } from "@src/app/hooks/useWindowSize";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { IS_PLAYWRIGHT } from "@util/constant";
import { gaEventTracker, getHomePageJSONLD } from "@util/helpers";
import { useDetectAdBlock } from "adblock-detect-react";

import OpinionFeatures from "./OpinionFeatures/OpinionFeatures";

type HomeContentProps = Pick<HomeContext, "data">;

export default function HomeContent({
  data: {
    title,
    groupedOverviewProps,
    overviewProps,
    breakingNewsProps,
    opinionFeaturesProps,
    seoFooterData,
    companiesMarketsProps,
    verticalsProps,
    podcastProps,
    skyboxProps,
    lifestyle,
    lifestyleCarousel,
    myBTData,
    newsBarProps,
  },
}: HomeContentProps): React.ReactElement {
  useHomepageRedirect();
  const { isScreenLG } = useWindowSize();

  const [myBTVisible, setMyBTVisible] = useState(true);

  const adBlockDetected = useDetectAdBlock();
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const { gaVariant } = useGetPosthogVariant({
    flag: "feature_enable_chinese_edition",
    isEnabledFeatureFlagVariant: true,
  });

  const { ref: myBTRef, inView: myBTBlock } = useInView();

  if (myBTBlock && myBTVisible) {
    gaEventTracker("mybt", "visible", window.location.href);
    setMyBTVisible(false);
  }

  const isUserLogin = OKTAUserInfo?.loginid;

  return (
    <div className="w-full" data-testid="home-content-component">
      <AdSettings
        adNames={["lb1", "imu2", "prestitial", "catfish"]}
        path="/homepage"
        adTargets={[{ key: "page", value: "home" }]}
      />

      <MetaTags
        title={title}
        ogTitle={title}
        description="The Business Times - Singapore's leading business and financial news source. Find latest business news, market analysis, and economic insights from Singapore and Asia-Pacific."
        ogDescription="The Business Times - Singapore's leading business and financial news source. Find latest business news, market analysis, and economic insights from Singapore and Asia-Pacific."
        ogType="website"
        articlePageclass="frontpage"
        slug="/"
        keywords={[
          "singapore budget 2015",
          "singapore",
          "budget 2015",
          "business & financial news",
        ]}
        jsonLd={getHomePageJSONLD()}
        alternates={[
          { hrefLang: "en-sg", href: "https://www.businesstimes.com.sg/" },
          { hrefLang: "en", href: "https://www.businesstimes.com.sg/global" },
          { hrefLang: "x-default", href: "https://www.businesstimes.com.sg/" },
        ]}
        ogLocale="en_SG"
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header isFromHome isDisplayEditionSwitcher />

      <HomepageMasthead />

      <GAData
        title="BT Homepage"
        level2="home"
        adblocker={adBlockDetected ? 1 : 0}
        abVariant={gaVariant}
      />

      <PulseEntry />

      <Container rootClassName="pt-1 text-gray-850">
        <>{!isScreenLG ? <SGBudget2026Banner className="my-4" /> : null}</>

        <SubMobileNavigation />

        <>
          {isScreenLG ? (
            <NewsBar
              cardProps={newsBarProps}
              className="mb-6"
              label={{
                text: "Budget 2026",
                link: "/keywords/singapore-budget-2026",
              }}
              eventCategory="singapore budget 2026"
            />
          ) : null}
        </>

        <HomepageTopOverview
          cardProps={overviewProps}
          groupedCardProps={groupedOverviewProps}
          className="mb-10"
        />

        <HomeSkybox cardProps={skyboxProps} className="mb-10" />

        <BreakingNews
          cardProps={breakingNewsProps}
          className="mb-10 border-t border-black pt-3"
        />

        <>
          {isUserLogin ? (
            <div ref={myBTRef} className="mb-10 w-full">
              <MyBTHomeCarousel
                results={myBTData}
                keywords={myBTRecommendedKeywords}
                title="INTRODUCING"
                showTooltip={false}
                rootClassName="border-t pt-3 border-black"
                disablePaywall={
                  OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
                }
                testId="homepage-mybt"
                header={
                  <BlockTitle
                    text="MYBT"
                    link={RouteFactory.myAccount}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const target = e.target as HTMLAnchorElement;
                      const label = target.href
                        ? `see more - ${target.href}`
                        : "see more";
                      gaEventTracker("mybt", "click", label);
                    }}
                  />
                }
                refCode="homepage-mybt"
              />
            </div>
          ) : null}
        </>

        <div className="mb-3 h-[1px] w-full bg-gray-850"></div>

        <div className="grid lg:grid-cols-3">
          <CompaniesMarkets
            cardProps={companiesMarketsProps}
            className="mb-10 border-gray-175 lg:col-span-2 lg:border-r lg:pr-6"
          />

          <div className="mb-10" data-testid="homepage-st-stocks">
            {IS_PLAYWRIGHT ? (
              <div className="flex h-[537px] w-full items-center justify-center border-gray-700 bg-gray-500 text-gray-700">
                ST INDEX
              </div>
            ) : (
              <iframe
                className="w-full lg:pl-3"
                src="https://sph.shareinvestor.com/bt_stock_chart_frame_2.html"
                title="BT Stock Chart"
                loading="lazy"
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                width="100%"
                height={537}
              ></iframe>
            )}
          </div>
        </div>

        <OpinionFeatures
          cardProps={opinionFeaturesProps}
          className={"mb-10 border-t border-black pt-3"}
        />

        <Verticals cardProps={verticalsProps} className="mb-10" />

        <div className="mb-3 h-[1px] w-full bg-gray-850"></div>

        <div className="grid lg:grid-cols-3">
          <Podcasts
            cardProps={podcastProps}
            className="mb-10 border-gray-175 lg:col-span-2 lg:border-r lg:pr-6"
          />

          <div className="mb-10 w-full lg:pl-6">
            <div className="mb-3 block h-[1px] w-full bg-gray-850 lg:hidden"></div>
            <Video testId="homepage-videos" />
          </div>
        </div>

        <div className="mb-3 h-[1px] w-full bg-gray-850"></div>

        <HomepageCarouselNewsletter
          rootClassName="mb-10"
          testId="homepage-carousel-newsletter-component"
          newsletterToSkip={NEWSLETTER_TYPE.BTGLOBAL}
        />
      </Container>

      <div className="mb-10 w-full bg-gray-850 py-10">
        <Container>
          <Lifestyle
            lifestyleCardProps={lifestyle}
            carouselCardProps={lifestyleCarousel}
          />
        </Container>
      </div>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
}
